import { useState } from "react";
import { CheckCircle2, XCircle, Info, AlertTriangle } from "lucide-react";
import StepShell from "./StepShell";
import { PROTECTION_PRODUCTS } from "../../data/protectionProducts";

/**
 * Step 8 — Required Protection.
 *
 * Three products require an explicit Accept / Decline decision before the
 * customer can continue:
 *   • Paint & Fabric Defense
 *   • GAP Protection
 *   • Vehicle Service Contract
 *
 * Decline flow per product:
 *   • All three: "Are you sure?" modal explaining why it may help.
 *   • Paint & Fabric Defense: after the first "Are you sure?", we offer a
 *     SECOND chance at the discounted price ($754 — comes from
 *     PROTECTION_PRODUCTS[].secondChancePrice). If they decline again,
 *     allow declined.
 *   • GAP + Service Contract: no discount. After the "Are you sure?"
 *     message, declining again is final.
 *
 * The customer cannot leave this step until every required product has a
 * final accepted/declined status.
 */
const REQUIRED_SLUGS = ["paint-fabric-defense", "gap-protection", "vehicle-service-contract"];

export default function RequiredProtectionStep({ byod, onContinue, onBack, firstName }) {
  const section = byod.state.required_protection || { states: {}, expanded: {} };
  const states = section.states || {};
  const expanded = section.expanded || {};

  const [confirmFor, setConfirmFor] = useState(null); // { slug, product, stage: 1|2 }

  const products = REQUIRED_SLUGS.map((slug) =>
    PROTECTION_PRODUCTS.find((p) => p.slug === slug)
  ).filter(Boolean);

  const writeState = (slug, patch) => {
    byod.replaceSection("required_protection", {
      ...section,
      states: { ...states, [slug]: { ...(states[slug] || {}), ...patch } },
    });
  };

  const writeExpanded = (slug, panel) => {
    const cur = expanded[slug];
    byod.replaceSection("required_protection", {
      ...section,
      expanded: { ...expanded, [slug]: cur === panel ? null : panel },
    });
  };

  const onAccept = (product, price) => {
    writeState(product.slug, { status: "accepted", finalPrice: price ?? Number(product.price) ?? 0, decline_step: 0 });
    setConfirmFor(null);
  };

  const onDeclineClick = (product) => {
    const cur = states[product.slug] || {};
    // Stage 1: open "Are you sure" modal
    const isPaintFabric = product.slug === "paint-fabric-defense";
    const stage = (cur.decline_step || 0) >= 1 && isPaintFabric ? 2 : 1;
    setConfirmFor({ slug: product.slug, product, stage });
  };

  const confirmDeclineFinal = () => {
    if (!confirmFor) return;
    writeState(confirmFor.slug, {
      status: "declined",
      finalPrice: null,
      decline_step: 99, // sentinel: final
    });
    setConfirmFor(null);
  };

  const advanceDeclineStage = () => {
    if (!confirmFor) return;
    // Mark we've shown the first "Are you sure?" — next decline opens the discount offer (paint-fabric).
    const slug = confirmFor.slug;
    writeState(slug, { decline_step: 1, status: null, finalPrice: null });
    setConfirmFor(null);
  };

  const allDecided = REQUIRED_SLUGS.every(
    (slug) => states[slug]?.status === "accepted" || states[slug]?.status === "declined"
  );

  return (
    <StepShell
      title={firstName ? `${firstName}, three quick protection decisions.` : "Three quick protection decisions."}
      subtitle="Each of these needs an Accept or Decline before we move on. You can change your mind later from the review screen."
      onBack={onBack}
      onContinue={onContinue}
      continueDisabled={!allDecided}
      testIdPrefix="byod-required-protection-step"
    >
      {!allDecided && (
        <div className="mb-4 rounded-xl bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-900" data-testid="byod-required-protection-hint">
          Accept or Decline each of the three products below to continue.
        </div>
      )}

      <div className="grid md:grid-cols-3 gap-4" data-testid="byod-required-protection-grid">
        {products.map((product) => {
          const st = states[product.slug] || {};
          const panel = expanded[product.slug];
          const isAccepted = st.status === "accepted";
          const isDeclined = st.status === "declined";
          const displayPrice = isAccepted && Number(st.finalPrice) > 0
            ? Number(st.finalPrice)
            : Number(product.price) || null;

          return (
            <div
              key={product.slug}
              className={`relative bg-white border-2 rounded-2xl p-4 flex flex-col transition ${
                isAccepted ? "border-emerald-500 bg-emerald-50/40"
                : isDeclined ? "border-[var(--ssc-line)]"
                : st.decline_step === 1 ? "border-amber-400 bg-amber-50/30"
                : "border-[var(--ssc-line)]"
              }`}
              data-testid={`byod-required-card-${product.slug}`}
            >
              <div className="flex items-start gap-3">
                <div className={`w-11 h-11 rounded-xl grid place-items-center shrink-0 ${
                  isAccepted ? "bg-emerald-500 text-white" : "bg-[var(--ssc-blue)]/10 text-[var(--ssc-blue)]"
                }`}>
                  <product.Icon size={20} />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="font-extrabold text-[var(--ssc-navy)] leading-tight">{product.name}</div>
                  {displayPrice ? (
                    <div className="text-sm font-black text-[var(--ssc-navy)] mt-0.5">
                      ${displayPrice.toLocaleString()}
                      {product.slug === "paint-fabric-defense" && isAccepted && st.finalPrice === product.secondChancePrice && (
                        <span className="ml-1 text-[10px] uppercase tracking-widest text-emerald-700">Saved</span>
                      )}
                    </div>
                  ) : null}
                  <p className="text-xs text-[var(--ssc-ink-2)] mt-1.5 leading-snug">{product.shortDesc}</p>
                </div>
              </div>

              {isAccepted && (
                <div className="mt-3 flex items-center gap-1.5 text-xs font-bold text-emerald-700" data-testid={`byod-required-accepted-${product.slug}`}>
                  <CheckCircle2 size={14} /> Added to your deal
                </div>
              )}
              {isDeclined && (
                <div className="mt-3 flex items-center gap-1.5 text-xs font-bold text-[var(--ssc-ink-2)]" data-testid={`byod-required-declined-${product.slug}`}>
                  <XCircle size={14} /> Declined — not included
                </div>
              )}

              <div className="mt-3 flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => onAccept(product, Number(product.price) || 0)}
                  className={`ssc-btn !py-2 !text-xs ${isAccepted ? "ssc-btn-primary" : "ssc-btn-outline"}`}
                  data-testid={`byod-required-accept-${product.slug}`}
                >
                  {isAccepted ? "Accepted" : "Accept"}
                </button>
                <button
                  type="button"
                  onClick={() => onDeclineClick(product)}
                  className={`ssc-btn !py-2 !text-xs ${isDeclined ? "ssc-btn-secondary" : "ssc-btn-outline"}`}
                  data-testid={`byod-required-decline-${product.slug}`}
                >
                  {isDeclined ? "Declined" : "Decline"}
                </button>
                <button
                  type="button"
                  onClick={() => writeExpanded(product.slug, "learn")}
                  className="ssc-btn ssc-btn-ghost !py-2 !text-xs"
                  data-testid={`byod-required-learn-${product.slug}`}
                  aria-expanded={panel === "learn"}
                >
                  <Info size={13} /> {panel === "learn" ? "Hide details" : "Learn more"}
                </button>
              </div>

              {panel === "learn" && (
                <div className="mt-3 pt-3 border-t border-[var(--ssc-line)]" data-testid={`byod-required-learn-panel-${product.slug}`}>
                  <p className="text-sm text-[var(--ssc-ink)] leading-relaxed">{product.whatItIs || product.heroText}</p>
                  {product.mayHelpWith?.length > 0 && (
                    <div className="mt-3">
                      <div className="text-[10px] font-extrabold uppercase tracking-widest text-[var(--ssc-blue)]">May help with</div>
                      <ul className="mt-1 list-disc ml-5 text-sm text-[var(--ssc-ink)] space-y-0.5">
                        {product.mayHelpWith.slice(0, 6).map((item, i) => <li key={i}>{item}</li>)}
                      </ul>
                    </div>
                  )}
                  {product.limits && <p className="text-xs text-[var(--ssc-ink-2)] mt-3 leading-snug">{product.limits}</p>}
                  <button
                    type="button"
                    onClick={() => writeExpanded(product.slug, "learn")}
                    className="text-[11px] font-bold uppercase tracking-widest text-[var(--ssc-blue)] mt-3 hover:underline"
                  >
                    Hide details
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Decline confirmation modal — STAGE 1 (all three) and STAGE 2 (paint-fabric discount) */}
      {confirmFor && (
        <DeclineConfirmModal
          info={confirmFor}
          onCancel={() => setConfirmFor(null)}
          onConfirmFinalDecline={confirmDeclineFinal}
          onAdvanceToDiscount={advanceDeclineStage}
          onAcceptAtDiscount={(price) => onAccept(confirmFor.product, price)}
          onAcceptAtFullPrice={(price) => onAccept(confirmFor.product, price)}
        />
      )}
    </StepShell>
  );
}

function DeclineConfirmModal({ info, onCancel, onConfirmFinalDecline, onAdvanceToDiscount, onAcceptAtDiscount, onAcceptAtFullPrice }) {
  const { product, stage } = info;
  const isPaintFabric = product.slug === "paint-fabric-defense";
  const secondChance = Number(product.secondChancePrice) || null;

  if (isPaintFabric && stage === 2 && secondChance) {
    return (
      <ModalShell title={`Before you go — try Paint & Fabric for $${secondChance.toLocaleString()}?`}>
        <p className="text-sm text-[var(--ssc-ink)] leading-relaxed">
          One last option. Paint & Fabric Defense is normally <strong>${Number(product.price).toLocaleString()}</strong>.
          If you accept now, we can include it at the discounted price of <strong>${secondChance.toLocaleString()}</strong>.
        </p>
        <p className="text-xs text-[var(--ssc-ink-2)] mt-2 leading-snug">
          Helps protect interior stains, road salt, weather exposure, fading, paint chips,
          and the kind of everyday damage that can happen after you drive it home.
        </p>
        <div className="mt-5 flex flex-wrap gap-2 justify-end">
          <button type="button" onClick={onConfirmFinalDecline} className="ssc-btn ssc-btn-outline" data-testid="byod-required-decline-final">
            Still decline
          </button>
          <button type="button" onClick={() => onAcceptAtDiscount(secondChance)} className="ssc-btn ssc-btn-primary" data-testid="byod-required-decline-accept-discount">
            Add at ${secondChance.toLocaleString()}
          </button>
        </div>
      </ModalShell>
    );
  }

  // Stage 1 — "Are you sure?" with copy from the product
  return (
    <ModalShell title={product.declineHeading || `Are you sure you want to decline ${product.name}?`}>
      <p className="text-sm text-[var(--ssc-ink)] leading-relaxed">
        {product.declineText || `${product.name} is designed to help reduce certain risks. Take a quick look before you decline.`}
      </p>
      {product.mayHelpWith?.length > 0 && (
        <div className="mt-3">
          <div className="text-[10px] font-extrabold uppercase tracking-widest text-[var(--ssc-blue)]">May help with</div>
          <ul className="mt-1 list-disc ml-5 text-sm text-[var(--ssc-ink)] space-y-0.5">
            {product.mayHelpWith.slice(0, 5).map((item, i) => <li key={i}>{item}</li>)}
          </ul>
        </div>
      )}
      <div className="mt-5 flex flex-wrap gap-2 justify-end">
        <button type="button" onClick={() => onAcceptAtFullPrice(Number(product.price) || 0)} className="ssc-btn ssc-btn-primary" data-testid="byod-required-decline-keep">
          Keep {product.name}
        </button>
        {isPaintFabric ? (
          <button type="button" onClick={onAdvanceToDiscount} className="ssc-btn ssc-btn-outline" data-testid="byod-required-decline-next">
            Decline — show me one more option
          </button>
        ) : (
          <button type="button" onClick={onConfirmFinalDecline} className="ssc-btn ssc-btn-outline" data-testid="byod-required-decline-final">
            Yes, decline
          </button>
        )}
        <button type="button" onClick={onCancel} className="ssc-btn ssc-btn-ghost" data-testid="byod-required-decline-cancel">
          Cancel
        </button>
      </div>
    </ModalShell>
  );
}

function ModalShell({ title, children }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/55 grid place-items-center p-3" data-testid="byod-required-decline-modal">
      <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-5 sm:p-6">
        <div className="flex items-start gap-2.5">
          <AlertTriangle size={18} className="text-amber-700 mt-0.5 shrink-0" />
          <h3 className="font-extrabold text-[var(--ssc-navy)] leading-tight">{title}</h3>
        </div>
        <div className="mt-3">{children}</div>
      </div>
    </div>
  );
}
